// pages/master-data/set-driver/SetDriverAddEdit.jsx
'use client';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Button, CircularProgress, Chip, Grid } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../../components/FormDialog';
import PickerField from '../../../../components/PickerField';
import PickerFieldTable from '../../../../components/PickerFieldTable';
import { useNotifier } from '../../../../components/Notifier';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useLanguage } from '../../../../utils/i18n';

import { createDriverAssign, updateDriverAssign, getDriverAssignById, useDriverAssignList } from '../../../../stores/driverAssignStore';
import { useMasterVehicleList } from '../../../../stores/masterVehicleStore';
import { useDriverList } from '../../../../stores/driverStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import MasterDriverAddEdit from '../MasterDriverAddEdit';
import MasterVehicleAddEdit from '../../vehicle/MasterVehicleAddEdit';

const baseInitial = {
  company: null,
  company_name: '',
  driver: null,
  driver_name: '',
  vehicle: null,
  vehicle_name: '',
  license_plate: '',
  body_name: '',
  imei_no: '',
  valid_at: null,
  exp_at: null
};

export default function SetDriverAddEdit({ open, id, companyOptions = [], onClose, onSuccess }) {
  const formikRef = useRef();
  const { t } = useLanguage();
  const notify = useNotifier();
  const [initialValues, setInitialValues] = useState(baseInitial);
  const [loading, setLoading] = useState(false);

  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const { data: assignmentList = [] } = useDriverAssignList(0, 2000, 'driver_name', '', '');

  const assignedDriverIds = useMemo(() => {
    return new Set((assignmentList || []).map((a) => String(a.driver || a.driver)));
  }, [assignmentList]);

  const assignedVehicleIds = useMemo(() => {
    return new Set((assignmentList || []).map((a) => String(a.vehicle || a.vehicle)));
  }, [assignmentList]);

  const driverPickerParams = usePickerPagination({
    fetchHook: useDriverList,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      state.companyId ? [{ value: state.companyId }] : [],
      state.searchKey,
      state.searchValue
    ],
    formatOption: (p) => {
      const dId = String(p.id ?? p.driver);
      const isAssigned = assignedDriverIds.has(dId);
      return {
        ...p,
        id: dId,
        name: p.name ?? p.driver_name ?? p.label ?? String(dId),
        status: isAssigned ? t('masterDriver.assigned', 'Assigned') : t('masterDriver.available', 'Available')
      };
    },
    searchKey: 'name',
    companyId: selectedCompanyId
  });

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      state.companyId ? [{ value: state.companyId }] : [],
      state.searchKey,
      state.searchValue
    ],
    formatOption: (p) => {
      const vId = String(p.id ?? p.vehicle);
      const isAssigned = assignedVehicleIds.has(vId);
      return {
        ...p,
        id: vId,
        name: p.name ?? p.vehicle_name ?? '',
        plate: p.license_plate ?? '',
        body_name: p.body_name ?? p.join?.body_name ?? '',
        imei_no: p.imei_no ?? p.imei ?? p.join?.imei_no ?? '',
        status: isAssigned ? t('masterDriver.assigned', 'Assigned') : t('masterDriver.available', 'Available')
      };
    },
    searchKey: 'license_plate',
    companyId: selectedCompanyId
  });

  const vehicleColumns = [
    { field: 'name', headerName: t('setDriver.vehicleName', 'Vehicle Name'), width: 200 },
    { field: 'plate', headerName: t('setDriver.licensePlate', 'License Plate'), width: 150 },
    { field: 'body_name', headerName: t('setDriver.bodyName', 'Body Name'), width: 150 },
    {
      field: 'status',
      headerName: t('general.status', 'Status'),
      width: 120,
      renderCell: (row) => (
        <Chip
          label={row.status}
          color={row.status === t('masterDriver.available', 'Available') ? 'success' : 'warning'}
          size="small"
          variant="outlined"
        />
      )
    }
  ];

  const driverColumns = [
    { field: 'name', headerName: t('setDriver.driverName', 'Driver Name'), width: 250 },
    {
      field: 'status',
      headerName: t('general.status', 'Status'),
      width: 120,
      renderCell: (row) => (
        <Chip
          label={row.status}
          color={row.status === t('masterDriver.available', 'Available') ? 'success' : 'warning'}
          size="small"
          variant="outlined"
        />
      )
    }
  ];

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchEditData = async () => {
      if (!id) {
        const storedCompanyId = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
        let defaultCompany = storedCompanyId;
        let defaultCompanyName = storedCompanyId;

        if (storedCompanyId && companyOptions.length > 0) {
          const match = companyOptions.find((c) => String(c.value) === String(storedCompanyId));
          if (match) {
            defaultCompany = String(match.value);
            defaultCompanyName = match.label;
          }
        }

        setInitialValues({
          ...baseInitial,
          company: defaultCompany,
          company_name: defaultCompanyName
        });
        setSelectedCompanyId(defaultCompany);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await getDriverAssignById(id);
        if (!mounted) return;
        if (data) {
          const comp = data.company ?? data.company_id ?? null;
          setInitialValues({
            company: comp,
            company_name: data.company_name ?? data.join?.company_name ?? '',
            driver: data.driver ?? data.driver_id ?? null,
            driver_name: data.driver_name ?? data.join?.driver_name ?? '',
            vehicle: data.vehicle ?? data.vehicle_id ?? null,
            vehicle_name: data.vehicle_name ?? data.join?.vehicle_name ?? '',
            license_plate: data.license_plate ?? data.join?.license_plate ?? '',
            body_name: data.body_name ?? data.join?.body_name ?? '',
            imei_no: data.imei_no ?? data.join?.imei_no ?? '',
            valid_at: data.valid_at ? dayjs(data.valid_at).format('YYYY-MM-DD') : null,
            exp_at: data.exp_at ? dayjs(data.exp_at).format('YYYY-MM-DD') : null
          });
          setSelectedCompanyId(comp);
        }
      } catch (err) {
        console.error('Failed to load assignment data:', err);
        notify({ message: t('setDriver.failedLoadAssignment', 'Failed to load assignment data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchEditData();
    return () => {
      mounted = false;
    };
  }, [id, open, companyOptions, notify, t]);

  const getOptionId = (o) => (o == null ? o : (o.value ?? o.id ?? o));
  const getOptionLabel = (o) => (o == null ? '' : (o.label ?? o.name ?? String(o.value ?? o.id ?? o)));
  const findOptionByValue = (options = [], value) => {
    if (value == null || value === '') return null;
    return (options || []).find((o) => String(getOptionId(o)) === String(value)) ?? null;
  };

  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required(t('validation.companyRequired', 'Company is required')),
    driver: Yup.mixed().required(t('validation.driverRequired', 'Driver is required')),
    vehicle: Yup.mixed().required(t('validation.vehicleRequired', 'Vehicle is required')),
    valid_at: Yup.string().nullable().required(t('validation.validDateRequired', 'Valid date is required'))
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const payload = {
        company: values.company ? Number(values.company) : null,
        driver: values.driver ? Number(values.driver) : null,
        driver_name: values.driver_name ?? '',
        exp_at: values.exp_at ? dayjs(values.exp_at).format('YYYY-MM-DD') : null,
        id: id ?? 0,
        imei_no: values.imei_no ?? '',
        license_plate: values.license_plate ?? '',
        valid_at: values.valid_at ? dayjs(values.valid_at).format('YYYY-MM-DD') : null,
        vehicle: values.vehicle ? Number(values.vehicle) : null,
        vehicle_name: values.vehicle_name ?? ''
      };

      if (id) {
        await updateDriverAssign(id, payload);
        notify({ message: t('setDriver.assignmentUpdated', 'Assignment updated successfully'), severity: 'success' });
      } else {
        await createDriverAssign(payload);
        notify({ message: t('setDriver.assignmentCreated', 'Assignment created successfully'), severity: 'success' });
        // Bersihkan input setelah tambah data berhasil
        if (formikRef.current) formikRef.current.resetForm();
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Submit error:', err);
      const errMsg = err?.response?.data?.message || err?.message || t('setDriver.failedSaveAssignment', 'Failed to save assignment');
      notify({ message: errMsg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          const { values, touched, errors, setFieldValue, isSubmitting } = formik;

          const validAtDate = values.valid_at ? dayjs(values.valid_at) : null;
          const expAtDate = values.exp_at ? dayjs(values.exp_at) : null;

          return (
            <FormDialog
              open={open}
              onClose={onClose}
              title={id ? t('setDriver.editDriverAssignment', 'Edit Driver Assignment') : t('setDriver.assignDriver', 'Assign Driver')}
              maxWidth="md"
              fullHeight={false}
              actions={
                <>
                  <Button onClick={onClose} disabled={isSubmitting || loading} variant="outlined">
                    {t('buttonTitle.cancel', 'Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    form="set-driver-form"
                    disabled={isSubmitting || loading}
                    sx={{ backgroundColor: '#0072DA' }}
                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                  >
                    {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
                  </Button>
                </>
              }
            >
              <Box>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <form id="set-driver-form" onSubmit={formik.handleSubmit}>
                    <Grid container spacing={3}>
                      {/* Select Company (Tetap pakai PickerField biasa) */}
                      <Grid size={{ xs: 12, md: 12 }}>
                        <PickerField
                          label={`${t('general.company', 'Company')} *`}
                          value={values.company}
                          options={companyOptions}
                          onChange={(opt) => {
                            const selected = findOptionByValue(companyOptions, opt) ?? (typeof opt === 'object' ? opt : null);
                            const selectedId = selected ? getOptionId(selected) : (opt ?? null);
                            setFieldValue('company', selectedId);
                            setSelectedCompanyId(selectedId);
                            if (selected) setFieldValue('company_name', getOptionLabel(selected));
                            // Reset driver & vehicle fields when company changes
                            setFieldValue('driver', null);
                            setFieldValue('driver_name', '');
                            setFieldValue('vehicle', null);
                            setFieldValue('vehicle_name', '');
                            setFieldValue('license_plate', '');
                          }}
                          getOptionId={(o) => getOptionId(o)}
                          getOptionLabel={(o) => getOptionLabel(o)}
                          dialogTitle={t('general.selectCompany', 'Select Company')}
                          error={touched.company && !!errors.company}
                          helperText={touched.company && errors.company}
                        />
                      </Grid>

                      {/* Select Driver (MENGGUNAKAN PickerFieldTable) */}
                      <Grid size={{ xs: 6, md: 6 }}>
                        <PickerFieldTable
                          label={t('setDriver.driver', 'Driver')}
                          placeholder={t('setDriver.selectDriver', 'Select Driver...')}
                          dialogTitle={t('setDriver.selectDriverTable', 'Select Driver from Table')}
                          value={values.driver}
                          {...driverPickerParams}
                          options={driverPickerParams.options}
                          columns={driverColumns}
                          onChange={(optId) => {
                            const selected = driverPickerParams.options.find((d) => String(d.id) === String(optId));
                            setFieldValue('driver', optId);
                            setFieldValue('driver_name', selected?.name || '');
                          }}
                          allowAdd
                          AddComponent={MasterDriverAddEdit}
                          getOptionId={(o) => o?.id}
                          getOptionLabel={(o) => o?.name}
                          required
                          error={Boolean(touched.driver && errors.driver)}
                          helperText={touched.driver && errors.driver}
                          filterField="status"
                          filterOptions={[
                            { label: t('general.searchAll', 'All'), value: 'all' },
                            { label: t('masterDriver.available', 'Available'), value: 'Available' },
                            { label: t('masterDriver.assigned', 'Assigned'), value: 'Assigned' }
                          ]}
                        />
                      </Grid>

                      {/* Valid At (Start Date) */}
                      <Grid size={{ xs: 6, md: 6 }}>
                        <DatePicker
                          format="DD-MM-YYYY"
                          label={`${t('setDriver.validAt', 'Valid At')} *`}
                          value={validAtDate}
                          maxDate={expAtDate || undefined}
                          onChange={(newVal) => {
                            setFieldValue('valid_at', newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : null);
                          }}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              error: Boolean(touched.valid_at && errors.valid_at),
                              helperText: touched.valid_at && errors.valid_at,
                              InputLabelProps: {
                                sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
                              }
                            }
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 6, md: 6 }}>
                        <PickerFieldTable
                          label={t('setDriver.vehicle', 'Vehicle')}
                          placeholder={t('setDriver.selectVehicle', 'Select Vehicle...')}
                          dialogTitle={t('setDriver.selectVehicleTable', 'Select Vehicle from Table')}
                          value={values.vehicle}
                          {...vehiclePickerParams}
                          options={vehiclePickerParams.options}
                          columns={vehicleColumns}
                          onChange={(optId) => {
                            const selected = vehiclePickerParams.options.find((v) => String(v.id) === String(optId));
                            setFieldValue('vehicle', optId);
                            setFieldValue('vehicle_name', selected?.name || '');
                            setFieldValue('license_plate', selected?.plate || '');
                            setFieldValue('body_name', selected?.body_name || '');
                            setFieldValue('imei_no', selected?.imei_no || '');
                          }}
                          getOptionId={(o) => o?.id}
                          allowAdd
                          AddComponent={MasterVehicleAddEdit}
                          getDisplayLabel={(o) => (o ? `${o.plate} - ${o.name}` : '')}
                          required
                          error={Boolean(touched.vehicle && errors.vehicle)}
                          helperText={touched.vehicle && errors.vehicle}
                          filterField="status"
                          filterOptions={[
                            { label: t('general.searchAll', 'All'), value: 'all' },
                            { label: t('masterDriver.available', 'Available'), value: 'Available' },
                            { label: t('masterDriver.assigned', 'Assigned'), value: 'Assigned' }
                          ]}
                        />
                      </Grid>

                      {/* Expired At (End Date) */}
                      <Grid size={{ xs: 6, md: 6 }}>
                        <DatePicker
                          format="DD-MM-YYYY"
                          label={t('setDriver.expiredAt', 'Expired At')}
                          value={expAtDate}
                          minDate={validAtDate || undefined}
                          onChange={(newVal) => {
                            setFieldValue('exp_at', newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : null);
                          }}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              InputLabelProps: {
                                sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
                              }
                            }
                          }}
                        />
                      </Grid>

                      {/* Body Name (Disabled)
                      <Grid size={{ xs: 6, md: 6 }}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Body Name"
                          name="body_name"
                          value={values.body_name ?? ''}
                          disabled
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid> */}
                    </Grid>
                  </form>
                )}
              </Box>
            </FormDialog>
          );
        }}
      </Formik>
    </LocalizationProvider>
  );
}
